import {
	IconDefinition,
	faAnchor,
	faBook,
	faBoxes,
	faBus,
	faChartPie,
	faCheese,
	faCircleChevronLeft,
	faCompactDisc,
	faExplosion,
	faLaptop,
	faPhone,
	faUserFriends,
} from "@fortawesome/free-solid-svg-icons";

import { SitePermsDto } from "../types/blastDtos";
import { OrgDto } from "../types/dto";

export interface RoutePermType {
	isOrgAdmin?: boolean;
	isDeveloper?: boolean;
	isBusinessManager?: boolean;
	isSubManager?: boolean;
	isSysadmin?: boolean;
	isTechSupport?: boolean;
	isSiteAdmin?: boolean;
	isSiteUser?: boolean;
	org?: OrgPermType;
	site?: SitePermsDto;
}

interface OrgPermType {
	isProductOwnerEnabled?: boolean;
	isServiceOwnerEnabled?: boolean;
}

export interface RouteType {
	path: string;
	breadcrumbs?: RouteType[];
	isSitePage?: boolean;
	title?: string;
	permissions?: RoutePermType;
}

const presentation = { path: "/", title: "Dashboard" } as RouteType;
const maintenance = { path: "/maintenance", title: "Maintenance" } as RouteType;
const dashboardOverview = {
	path: "/dashboard",
	title: "Dashboard",
} as RouteType;
const organizations = {
	path: "/organizations",
	title: "Organizations",
	permissions: {
		isSysadmin: true,
	},
} as RouteType;
const offerings = {
	path: "/offerings",
	title: "Offerings",
	permissions: {
		org: { isProductOwnerEnabled: true, isServiceOwnerEnabled: true },
		isOrgAdmin: true,
		isBusinessManager: true,
		isDeveloper: true,
		isTechSupport: true,
	},
} as RouteType;
const product = {
	path: offerings.path + "/product/:id",
	breadcrumbs: [offerings],
	title: "Product Details",
	permissions: offerings.permissions,
} as RouteType;
const service = {
	path: offerings.path + "/service/:id",
	breadcrumbs: [offerings],
	title: "Service Details",
	permissions: offerings.permissions,
} as RouteType;
const allUsers = {
	path: "/all-users",
	title: "All Users",
	permissions: {
		isSysadmin: true,
	},
} as RouteType;
const users = {
	path: "/users",
	title: "Users",
	permissions: {
		isOrgAdmin: true,
		isSubManager: true,
		isDeveloper: true,
	},
} as RouteType;
const licences = {
	path: "/licences",
	title: "Licences",
	permissions: {
		isOrgAdmin: true,
		isSubManager: true,
		isDeveloper: true,
	},
} as RouteType;
const issuedLicences = {
	path: "/issued-licences",
	title: "Issued Licences",
	permissions: {
		org: { isProductOwnerEnabled: true },
		isOrgAdmin: true,
		isBusinessManager: true,
	},
} as RouteType;
const issuedSubscriptions = {
	path: "/issued-subscriptions",
	title: "Customer Subs",
	permissions: {
		org: { isProductOwnerEnabled: true, isServiceOwnerEnabled: true },
		isOrgAdmin: true,
		isBusinessManager: true,
	},
} as RouteType;
const issuedSubscriptionDetails = {
	path: issuedSubscriptions.path + "/:id",
	breadcrumbs: [issuedSubscriptions],
	title: "Subscription Details",
	permissions: issuedSubscriptions.permissions,
} as RouteType;
const subbedSubs = {
	path: "/my-subscriptions",
	title: "Software",
	permissions: {
		isOrgAdmin: true,
		isSubManager: true,
	},
} as RouteType;
const serviceLicences = {
	path: subbedSubs.path + "/:id",
	title: "Service Licences",
	breadcrumbs: [subbedSubs],
	permissions: subbedSubs.permissions,
} as RouteType;
const emailPrefs = {
	path: "/email-preferences/:email/:code",
	title: "Email Preferences",
} as RouteType;
const orgEditor = {
	title: "Edit Organization",
	path: "/edit-org",
	permissions: {
		isOrgOwner: true,
	},
} as RouteType;
const home = { path: "/home", title: "Home" } as RouteType;
const orgId = {
	path: "/org/:orgId",
	title: "Org",
} as RouteType;
const notAuthenticated = {
	path: "/not-authenticated",
	title: "Not Authenticated",
} as RouteType;
const support = {
	path: "/support",
	title: "Support",
} as RouteType;

export const routes: { [key: string]: RouteType } = {
	// pages
	presentation,
	maintenance,
	dashboardOverview,
	organizations,
	offerings,
	product,
	service,
	allUsers,
	users,
	licences,
	issuedLicences,
	issuedSubscriptions,
	issuedSubscriptionDetails,
	subbedSubs,
	serviceLicences,
	emailPrefs,
	orgEditor,
	home,
	test: { path: "/test" },
	support,

	orgId,

	register: { path: "/Account/Register" },
	login: { path: "/Account/Login" },
	userProfile: { path: "/Account/Profile" },
	signInOidc: { path: "/signin-oidc" },
	signOutCallbackOidc: { path: "/signout-callback-oidc" },
	registerSubscription: { path: "/subscription/register" },
	registerExistingUserSub: { path: "/subscription/existing-reg" },
	notFound: { path: "/not-found" },
	notAuthenticated,
};

const blastPresentation = { path: "/", title: "Sites" } as RouteType;
const blastDashboardOverview = {
	path: "/dashboard",
	title: "Sites",
} as RouteType;
const blastsShared = {
	path: "/blasts-shared",
	title: "Blasts Shared",
} as RouteType;
const blastsSharedDetails = {
	path: blastsShared.path + "/:blastId",
	title: "Blast Viewer",
	breadcrumbs: [blastsShared],
} as RouteType;

const nodeLibrary = {
	path: "/node-libraries",
	title: "Node Libraries",
} as RouteType;
const nodeDetails = {
	path: nodeLibrary.path + "/:id",
	breadcrumbs: [nodeLibrary],
	title: "Node Libraries",
} as RouteType;
const sites = {
	path: "/sites",
	title: "Site Admin",
	permissions: {
		isOrgAdmin: true,
	},
} as RouteType;
const blastOverview = {
	path: "/",
	title: "Site Dashboard",
	isSitePage: true,
} as RouteType;
const siteUsers = {
	path: "/users",
	title: "Users",
	isSitePage: true,
	permissions: {
		isOrgAdmin: true,
		isSubManager: true,
		isSiteAdmin: true,
	},
} as RouteType;
const blasts = {
	path: "/blasts",
	title: "Blasts",
	isSitePage: true,
	permissions: {
		isOrgAdmin: true,
		isSubManager: true,
		isSiteAdmin: true,
		isSiteUser: true,
	},
} as RouteType;
const blastDetails = {
	path: "/blast/:blastId",
	title: "Blast Details",
	breadcrumbs: [blastOverview],
	isSitePage: true,
	permissions: {
		isOrgAdmin: true,
		isSubManager: true,
		isSiteAdmin: true,
		isSiteUser: true,
	},
} as RouteType;
const blastRedirect = {
	path: "/blast/:blastId",
	title: "Blast Redirect",
} as RouteType;

const siteNodeLibraries = {
	path: "/node-libraries",
	title: "Node Libraries",
	isSitePage: true,
	// permissions: {
	// 	isOrgAdmin: true,
	// 	isSubManager: true,
	// 	isSiteAdmin: true,
	// 	isSiteUser: true,
	// },
} as RouteType;
const sitesSysAdmin = {
	path: "/all-sites",
	title: "Site Admin",
	permissions: {
		isSysadmin: true,
	},
};
const blastsSysAdmin = {
	path: "/all-blasts",
	title: "Blast Admin",
	permissions: {
		isSysadmin: true,
	},
};
const nodeLibrariesSysAdmin = {
	path: "/all-nodelibs",
	title: "Node Lib Admin",
	permissions: {
		isSysadmin: true,
	},
};

const blastLink = {
	path: "/blast-link/:linkId",
	title: "Blast Link",
} as RouteType;
const blastLinkAnon = {
	path: "/blast-link-anon/:linkId",
	title: "Blast Link",
} as RouteType;

export const blastRoutes: { [key: string]: RouteType } = {
	home,
	maintenance,
	presentation: blastPresentation,
	dashboardOverview: blastDashboardOverview,
	nodeLibrary,
	nodeDetails,
	sites,
	sitesSysAdmin,
	blastsSysAdmin,
	nodeLibrariesSysAdmin,
	blastsShared,
	blastsSharedDetails,

	orgId,

	blastOverview,
	siteUsers,
	blasts,
	blastDetails,
	siteNodeLibraries,

	blastLink,
	blastLinkAnon,

	blastRedirect,

	signInOidc: { path: "/signin-oidc" },
	signOutCallbackOidc: { path: "/signout-callback-oidc" },

	notAuthenticated,
};

export interface NavItemType {
	route: RouteType;
	icon: IconDefinition;
	external?: boolean;
	blockedOrgs?: OrgDto[];
	allowedOrgs?: OrgDto[];
}

export interface RouteSettingType {
	title: string;
	navItems: NavItemType[];
}

export const navSectionsSettings: RouteSettingType[] = [
	{
		title: "Personal",
		navItems: [
			{
				route: routes.presentation,
				icon: faChartPie,
			},
		],
	},
	{
		title: "Sysadmin",
		navItems: [
			{
				route: routes.allUsers,
				icon: faBus,
			},
			{
				route: routes.organizations,
				icon: faAnchor,
			},
		],
	},
	{
		title: "Business",
		navItems: [
			{
				route: routes.issuedSubscriptions,
				icon: faCheese,
			},
			{
				route: routes.issuedLicences,
				icon: faLaptop,
			},
			{
				route: routes.offerings,
				icon: faBoxes,
			},
		],
	},
	{
		title: "My Org",
		navItems: [
			{
				route: routes.users,
				icon: faUserFriends,
			},
			{
				route: routes.licences,
				icon: faLaptop,
			},
			{
				route: routes.subbedSubs,
				icon: faCompactDisc,
			},
		],
	},
];

export const blockedOrgsFromBlast = [
	{
		id: "221c3f15-1e84-431a-1974-08daf8442809",
		name: "Rio Tinto",
	},
	{
		id: "963a73c0-e1ec-4cee-59c0-08daf4edca17",
		name: "My Org",
	},
];

export const allowedOrgsSupport = [
	{
		id: "221c3f15-1e84-431a-1974-08daf8442809",
		name: "Rio Tinto",
	},
	{
		id: "4bd7c37c-4d28-41bb-fc4c-08da693490e6",
		name: "Igneous Tech",
	},
	{
		id: "7692c15e-9749-40ab-b4ce-08da8c2a45f2",
		name: "Austin",
	},
];

export const navSectionsSettingsBottom: RouteSettingType[] = [
	{
		title: "",
		navItems: [
			{
				route: routes.support,
				allowedOrgs: allowedOrgsSupport,
				icon: faPhone,
			},
		],
	},
];

export const navSectionsSettingsBlast: RouteSettingType[] = [
	{
		title: "Sysadmin",
		navItems: [
			{
				route: blastRoutes.sitesSysAdmin,
				icon: faLaptop,
			},
			{
				route: blastRoutes.blastsSysAdmin,
				icon: faExplosion,
			},
			{
				route: blastRoutes.nodeLibrariesSysAdmin,
				icon: faBook,
			},
		],
	},
	{
		title: "Data",
		navItems: [
			{
				route: blastRoutes.presentation,
				icon: faChartPie,
			},
			{
				route: blastRoutes.blastsShared,
				icon: faExplosion,
			},
			{
				route: blastRoutes.nodeLibrary,
				icon: faBook,
			},
		],
	},
];

export const navSectionsSettingsBlastBottom: RouteSettingType[] = [
	{
		title: "",
		navItems: [],
	},
];

export const navSectionsSettingsBlastSite: RouteSettingType[] = [
	{
		title: "Personal",
		navItems: [
			{
				route: blastRoutes.blastOverview,
				icon: faChartPie,
			},
		],
	},
	{
		title: "Admin",
		navItems: [
			{
				route: blastRoutes.siteUsers,
				icon: faUserFriends,
			},
		],
	},
	{
		title: "Data",
		navItems: [
			{
				route: blastRoutes.blasts,
				icon: faExplosion,
			},
		],
	},
];

export const navSectionsSettingsBlastSiteBottom: RouteSettingType[] = [
	{
		title: "",
		navItems: [
			{
				route: blastRoutes.presentation,
				icon: faCircleChevronLeft,
			},
		],
	},
];
